/**
 * App Settings Context with React Query Caching
 * P1 Fix: Uses React Query for caching to prevent refetching on every mount
 * P2 Fix: Removed unnecessary timeout warning logs
 */

import { createContext, useContext, ReactNode, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { structuredLogger } from "@/lib/logging";

interface AppSettings {
  app_name: string;
  app_logo_url: string | null;
  dark_logo_url: string | null;
  primary_color: string;
  secondary_color: string;
  accent_color: string | null;
  font_family: string;
  allow_public_registration: boolean;
  explainer_video_url: string | null;
}

interface AppSettingsContextType {
  settings: AppSettings;
  loading: boolean;
  refetch: () => Promise<void>;
}

const defaultSettings: AppSettings = {
  app_name: "GG Life",
  app_logo_url: null,
  dark_logo_url: null,
  primary_color: "#10b981",
  secondary_color: "#6366f1",
  accent_color: null,
  font_family: "Inter",
  allow_public_registration: false,
  explainer_video_url: null,
};

const AppSettingsContext = createContext<AppSettingsContextType>({
  settings: defaultSettings,
  loading: true,
  refetch: async () => {},
});

export const AppSettingsProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();

  const { data: settings, isLoading, refetch } = useQuery({
    queryKey: ['application-settings'],
    queryFn: async (): Promise<AppSettings> => {
      const { data, error } = await supabase
        .from("application_settings")
        .select("*")
        .maybeSingle();

      if (error) {
        structuredLogger.warn("Error loading application settings", { error: error.message });
        return defaultSettings;
      }

      if (!data) {
        return defaultSettings;
      }

      return {
        app_name: data.app_name || "GG Life",
        app_logo_url: data.app_logo_url,
        dark_logo_url: data.dark_logo_url,
        primary_color: data.primary_color || "#10b981",
        secondary_color: data.secondary_color || "#6366f1",
        accent_color: data.accent_color,
        font_family: data.font_family || "Inter",
        allow_public_registration: data.allow_public_registration ?? false,
        explainer_video_url: data.explainer_video_url || null,
      };
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - P1 fix: long cache for app settings
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  });

  const value = useMemo(() => ({
    settings: settings ?? defaultSettings,
    loading: isLoading,
    refetch: async () => {
      await queryClient.invalidateQueries({ queryKey: ['application-settings'] });
      await refetch();
    },
  }), [settings, isLoading, queryClient, refetch]);

  return (
    <AppSettingsContext.Provider value={value}>
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettingsContext = () => useContext(AppSettingsContext);
